/**
 * +field: Mossvale field mobs (docs/design/zones.md Phase 1). MapleStory-dense fields want mobs
 * that die fast: at even level a fresh hero (2–3 core abilities, ≈ 3k DPS in L10 units) kills one
 * in ≈ 2–4 s, and pulling five at once is survivable, so AoE feels great.
 *
 * Numbers are in L10 units: the sim scales mobs AND heroes by levelMult(level) (L1 ≈ 0.46, L10 = 1),
 * so an even-level fight plays the same at every level. TIER_HP: fodder 92k, standard 165.6k,
 * caster 147.2k, big 262.2k. Hero (DPS) ≈ 42k HP. Field mobs give no M+ forces (forces 0).
 */
import type { EnemyAbility, NpcDef } from '@keystone/sim';
import { npcMelee } from '../balance';
import { enemy, hit, mech, telegraph } from '../npcs/common';
import { MOB, MOB_ABILITY, MOB_AURA } from './ids';

// ---------------------------------------------------------------------------
// Abilities (one or two simple, readable casts per family)
// ---------------------------------------------------------------------------

/** Bloom Slime: a slow lob of pollen. Interruptible, easy to out-range or kick. */
const pollenSpit = enemy({
  id: MOB_ABILITY.pollenSpit,
  name: 'Pollen Spit',
  school: 'nature',
  kind: 'bolt',
  description: 'A slow glob of sticky pollen at its target. Interruptible.',
  cast: { type: 'cast', timeMs: 2000 },
  interruptible: true,
  range: 25,
  timer: { first: 2500, every: 8000, jitter: 1500 },
  target: 'victim',
  counter: ['interrupt'],
  effects: [hit('nature', 1800)],
});

/** Tusk Boar: a short, telegraphed charge (the opener when you pull from range, then now and then). */
const tuskCharge = enemy({
  id: MOB_ABILITY.tuskCharge,
  name: 'Tusk Charge',
  school: 'physical',
  kind: 'charge',
  description: 'Paws the ground, then charges along a short line at its target. Step aside.',
  cast: { type: 'cast', timeMs: 1000 },
  range: 18,
  timer: { first: 800, every: 12000, jitter: 2000 },
  target: 'victim',
  shape: { type: 'line', length: 14, width: 3 },
  counter: ['dodge'],
  effects: [
    mech({
      kind: 'charge',
      rule: 'primary',
      width: 3,
      overshoot: 3,
      delayMs: 1000,
      aimFollowMs: 500,
      speed: 28,
      intent: 'avoid',
      effects: [hit('physical', 3000, { avoidable: false }), mech({ kind: 'knockback', distance: 3, durationMs: 300 })],
    }),
  ],
});

/** Bramble Wolf: a quick lunge (short cast so it reads on the nameplate). */
const lungingBite = enemy({
  id: MOB_ABILITY.lungingBite,
  name: 'Lunging Bite',
  school: 'physical',
  kind: 'tankbuster',
  description: 'A snapping lunge at its target.',
  cast: { type: 'cast', timeMs: 700 },
  range: 6,
  timer: { first: 3000, every: 9000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [hit('physical', 2400)],
});

/** Briar Bandit: a dirty slash that leaves a short bleed. */
const dirtySlash = enemy({
  id: MOB_ABILITY.dirtySlash,
  name: 'Dirty Slash',
  school: 'physical',
  kind: 'tankbuster',
  description: 'A rusty-blade slash at its target that bleeds for 6 s.',
  cast: { type: 'cast', timeMs: 1200 },
  range: 6,
  timer: { first: 4000, every: 11000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [
    hit('physical', 2040),
    {
      type: 'applyAura',
      aura: {
        id: MOB_AURA.briarBleed,
        name: 'Rusty Cut',
        kind: 'debuff',
        durationMs: 6000,
        stacking: 'refresh',
        periodic: { intervalMs: 2000, effects: [hit('physical', 300, { avoidable: false })] },
      },
    },
  ],
});

/** Bandit Slinger: a ranged sling stone (its "bolt"). Interruptible. */
const slingStone = enemy({
  id: MOB_ABILITY.slingStone,
  name: 'Sling Stone',
  school: 'physical',
  kind: 'bolt',
  description: 'Hurls a stone at its target from range. Interruptible.',
  cast: { type: 'cast', timeMs: 1600 },
  interruptible: true,
  range: 30,
  timer: { first: 1000, every: 4500, jitter: 1000 },
  target: 'victim',
  counter: ['interrupt'],
  effects: [hit('physical', 2300)],
});

/** Bandit Captain (elite): a big, slow, readable frontal on its target. */
const SWEEP_CONE = { type: 'cone' as const, range: 10, angle: (2 * Math.PI) / 3 };
const brutalSweep = enemy({
  id: MOB_ABILITY.brutalSweep,
  name: 'Brutal Sweep',
  school: 'physical',
  kind: 'frontal',
  description: 'Winds up a wide sweep in front of him. Get out of the orange cone (or Dodge through him).',
  cast: { type: 'cast', timeMs: 1500 },
  range: 8,
  timer: { first: 5000, every: 13000, jitter: 1500 },
  target: 'victim',
  shape: SWEEP_CONE,
  lockFacing: true,
  counter: ['dodge'],
  warning: { text: 'Brutal Sweep', severity: 'danger' },
  effects: [telegraph(SWEEP_CONE, 'caster', 1500, [hit('physical', 11000, { avoidable: false })])],
});

/** Bandit Captain: at half health he bellows and hits harder (an enrage: soothe or burn him). */
const rallyingBellow = enemy({
  id: MOB_ABILITY.rallyingBellow,
  name: 'Rallying Bellow',
  school: 'physical',
  kind: 'buff',
  description: 'At 50% health the Captain bellows: +25% damage and attack speed. Finish him fast.',
  cast: { type: 'cast', timeMs: 1000 },
  timer: { first: 0, every: 1000 },
  target: 'self',
  once: true,
  hpWindow: { max: 0.5 },
  priority: 10,
  counter: ['defensive'],
  warning: { text: 'Rallying Bellow', severity: 'info' },
  effects: [
    {
      type: 'applyAura',
      to: 'self',
      aura: {
        id: MOB_AURA.rallied,
        name: 'Rallied',
        kind: 'buff',
        durationMs: 0,
        stacking: 'ignore',
        dispelType: 'enrage',
        mods: [
          { stat: 'damageDonePct', value: 0.25 },
          { stat: 'hastePct', value: 0.25 },
        ],
      },
    },
  ],
});

// ---- +zone: Glimmermere, Greyhorn Heights, the Pale Rise -------------------------------------

/** Reedback Snapper: a pinching claw on its target (a heavier swing; use a defensive on a big pull). */
const clawSnap = enemy({
  id: MOB_ABILITY.clawSnap,
  name: 'Claw Snap',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Snaps a heavy claw shut on its target.',
  cast: { type: 'cast', timeMs: 900 },
  range: 6,
  timer: { first: 3500, every: 10000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [hit('physical', 2500)],
});

/** Mire Croaker: a lobbed glob of mud. Interruptible. */
const mudSpit = enemy({
  id: MOB_ABILITY.mudSpit,
  name: 'Mud Spit',
  school: 'nature',
  kind: 'bolt',
  description: 'Spits a glob of lake mud at its target. Interruptible.',
  cast: { type: 'cast', timeMs: 1800 },
  interruptible: true,
  range: 24,
  timer: { first: 2000, every: 7000, jitter: 1500 },
  target: 'victim',
  counter: ['interrupt'],
  effects: [hit('nature', 2000)],
});

/** Tallow Digger: an overhead pick swing. */
const pickSwing = enemy({
  id: MOB_ABILITY.pickSwing,
  name: 'Pick Swing',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Brings a mining pick down on its target.',
  cast: { type: 'cast', timeMs: 1100 },
  range: 6,
  timer: { first: 3000, every: 10000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [hit('physical', 2300)],
});

/** Tallow Chanter: a gout of candle-flame. Interruptible. */
const candleFlare = enemy({
  id: MOB_ABILITY.candleFlare,
  name: 'Candle Flare',
  school: 'fire',
  kind: 'bolt',
  description: 'Blows a burst of candle-flame at its target. Interruptible.',
  cast: { type: 'cast', timeMs: 1700 },
  interruptible: true,
  range: 26,
  timer: { first: 1500, every: 5500, jitter: 1000 },
  target: 'victim',
  counter: ['interrupt'],
  effects: [hit('fire', 2400)],
});

/** Pale Sentinel: a short, readable slam in front of it. */
const SLAM_CONE = { type: 'cone' as const, range: 7, angle: Math.PI / 2 };
const stoneSlam = enemy({
  id: MOB_ABILITY.stoneSlam,
  name: 'Stone Slam',
  school: 'physical',
  kind: 'frontal',
  description: 'Raises its stone fists and slams the ground in front of it. Step out of the cone.',
  cast: { type: 'cast', timeMs: 1400 },
  range: 6,
  timer: { first: 4000, every: 11000, jitter: 2000 },
  target: 'victim',
  shape: SLAM_CONE,
  lockFacing: true,
  counter: ['dodge'],
  effects: [telegraph(SLAM_CONE, 'caster', 1400, [hit('physical', 5200, { avoidable: false })])],
});

/** Hollow Shade: a bolt of cold shadow. Interruptible. */
const shadeBolt = enemy({
  id: MOB_ABILITY.shadeBolt,
  name: 'Shade Bolt',
  school: 'shadow',
  kind: 'bolt',
  description: 'Hurls a bolt of cold shadow at its target. Interruptible.',
  cast: { type: 'cast', timeMs: 1600 },
  interruptible: true,
  range: 28,
  timer: { first: 1500, every: 5000, jitter: 1000 },
  target: 'victim',
  counter: ['interrupt'],
  effects: [hit('shadow', 2600)],
});

export const FIELD_MOB_ABILITIES: EnemyAbility[] = [pollenSpit, tuskCharge, lungingBite, dirtySlash, slingStone, brutalSweep, rallyingBellow, clawSnap, mudSpit, pickSwing, candleFlare, stoneSlam, shadeBolt];

// ---------------------------------------------------------------------------
// NPCs
// ---------------------------------------------------------------------------

const critter = { radius: 0.7, combatReach: 1.2, moveSpeed: 6 };
const beast = { radius: 0.9, combatReach: 1.4, moveSpeed: 7 };
const humanoid = { radius: 0.9, combatReach: 1.5, moveSpeed: 7 };

export const FIELD_MOB_NPCS: NpcDef[] = [
  {
    // +specs: Warden Ilsa's practice dummy on the Tower Road (the Advancement Trial's "try it"). Never
    // swings, never hunted by Auto, shrugs off death (huge health) and resets 8 s after you stop.
    id: MOB.practiceDummy,
    name: 'Practice Dummy',
    tier: 'boss',
    hpMult: 1.1,
    forces: 0,
    abilities: [],
    radius: 1.2,
    combatReach: 2,
    moveSpeed: 0,
    practice: true,
    idleResetMs: 8000,
    color: '#95a5a6',
  },
  {
    // The treasure sprite (field ground loot): never fights, runs from you faster than you run
    // (8 vs 7 yd/s, so you catch it at corners or with a charge / slow / ranged burst), escapes
    // after 40 s. ≈ 9k HP at L10: a few hits once caught. Pays a big burst (sim field/loot.ts).
    id: MOB.treasureSprite,
    name: 'Treasure Sprite',
    tier: 'fodder',
    hpMult: 0.1,
    forces: 0,
    abilities: [],
    radius: 0.6,
    combatReach: 1.5,
    moveSpeed: 7.6,
    flees: true,
    color: '#ffd84a',
  },
  {
    // Passive (spawn aggroRadius 0): the first thing a new hero hits. ≈ 8.3k HP → ~3 s.
    id: MOB.meadowSlime,
    name: 'Meadow Slime',
    tier: 'fodder',
    hpMult: 0.09,
    forces: 0,
    melee: npcMelee(700, 2000),
    abilities: [],
    ...critter,
    moveSpeed: 5,
    color: '#8fd16a',
  },
  {
    // ≈ 10.3k HP; lobs Pollen Spit from short range.
    id: MOB.bloomSlime,
    name: 'Bloom Slime',
    tier: 'caster',
    hpMult: 0.07,
    forces: 0,
    melee: npcMelee(600, 2000),
    abilities: [MOB_ABILITY.pollenSpit],
    ...critter,
    moveSpeed: 5,
    preferredRange: 12,
    color: '#f29ad8',
  },
  {
    // ≈ 12.4k HP; Tusk Charge on the pull.
    id: MOB.tuskBoar,
    name: 'Tusk Boar',
    tier: 'standard',
    hpMult: 0.075,
    forces: 0,
    melee: npcMelee(1000, 2200),
    abilities: [MOB_ABILITY.tuskCharge],
    ...beast,
    radius: 1.1,
    combatReach: 1.6,
    color: '#a0714f',
  },
  {
    // ≈ 10.1k HP; fast swings.
    id: MOB.brambleWolf,
    name: 'Bramble Wolf',
    tier: 'fodder',
    hpMult: 0.11,
    forces: 0,
    melee: npcMelee(800, 1600),
    abilities: [MOB_ABILITY.lungingBite],
    ...beast,
    moveSpeed: 7.5,
    color: '#6b7a5a',
  },
  {
    // ≈ 13.2k HP. Damage ×0.85 after the grind harness (most Blademaster deaths at L7–10 were here).
    id: MOB.briarBandit,
    name: 'Briar Bandit',
    tier: 'standard',
    hpMult: 0.08,
    forces: 0,
    melee: npcMelee(1020, 2200),
    abilities: [MOB_ABILITY.dirtySlash],
    ...humanoid,
    color: '#8c5a3c',
  },
  {
    // ≈ 11k HP; keeps range and slings stones.
    id: MOB.banditSlinger,
    name: 'Bandit Slinger',
    tier: 'caster',
    hpMult: 0.075,
    forces: 0,
    melee: npcMelee(400, 2000),
    abilities: [MOB_ABILITY.slingStone],
    ...humanoid,
    preferredRange: 22,
    color: '#b08a4a',
  },
  {
    // Elite (long respawn): ≈ 91.8k HP → ~25–30 s solo at even level; a readable frontal + a 50% enrage.
    id: MOB.banditCaptain,
    name: 'Bandit Captain',
    tier: 'big',
    hpMult: 0.35,
    forces: 0,
    melee: npcMelee(2000, 2400),
    abilities: [MOB_ABILITY.brutalSweep, MOB_ABILITY.rallyingBellow],
    radius: 1.3,
    combatReach: 2,
    moveSpeed: 7,
    tauntDr: false,
    color: '#7a2f2f',
  },
  // ---- +zone ---------------------------------------------------------------------------------
  {
    // Glimmermere: crab-folk on the shores. ≈ 13k HP; a claw snap.
    id: MOB.reedbackSnapper,
    name: 'Reedback Snapper',
    tier: 'standard',
    hpMult: 0.08,
    forces: 0,
    melee: npcMelee(950, 2200),
    abilities: [MOB_ABILITY.clawSnap],
    ...beast,
    radius: 1.0,
    moveSpeed: 6,
    color: '#c8643e',
  },
  {
    // Glimmermere: frog-like croakers that spit mud from range. ≈ 10k HP.
    id: MOB.mireCroaker,
    name: 'Mire Croaker',
    tier: 'caster',
    hpMult: 0.068,
    forces: 0,
    melee: npcMelee(600, 2000),
    abilities: [MOB_ABILITY.mudSpit],
    ...critter,
    radius: 0.9,
    preferredRange: 14,
    color: '#6f8f3a',
  },
  {
    // Greyhorn: lean grey hounds of the foothills. ≈ 10k HP, fast swings.
    id: MOB.cragHound,
    name: 'Crag Hound',
    tier: 'fodder',
    hpMult: 0.11,
    forces: 0,
    melee: npcMelee(820, 1600),
    abilities: [MOB_ABILITY.lungingBite],
    ...beast,
    moveSpeed: 7.5,
    color: '#7d8794',
  },
  {
    // Greyhorn: the Tallow miners, small folk with candles on their caps. ≈ 13k HP.
    id: MOB.tallowDigger,
    name: 'Tallow Digger',
    tier: 'standard',
    hpMult: 0.08,
    forces: 0,
    melee: npcMelee(1000, 2200),
    abilities: [MOB_ABILITY.pickSwing],
    ...humanoid,
    radius: 0.8,
    color: '#8a6a44',
  },
  {
    // Greyhorn: their chanters keep back and blow candle-flame. ≈ 11k HP.
    id: MOB.tallowChanter,
    name: 'Tallow Chanter',
    tier: 'caster',
    hpMult: 0.075,
    forces: 0,
    melee: npcMelee(400, 2000),
    abilities: [MOB_ABILITY.candleFlare],
    ...humanoid,
    radius: 0.8,
    preferredRange: 20,
    color: '#b88a3a',
  },
  {
    // The Pale Rise: old stone guardians that still keep the ruins. ≈ 13k HP; a readable slam.
    id: MOB.paleSentinel,
    name: 'Pale Sentinel',
    tier: 'standard',
    hpMult: 0.08,
    forces: 0,
    melee: npcMelee(1100, 2400),
    abilities: [MOB_ABILITY.stoneSlam],
    ...humanoid,
    radius: 1.1,
    moveSpeed: 6.5,
    color: '#d8d4c8',
  },
  {
    // The Pale Rise: drifting shades from the barrow's dead. ≈ 11k HP; shadow bolts.
    id: MOB.hollowShade,
    name: 'Hollow Shade',
    tier: 'caster',
    hpMult: 0.075,
    forces: 0,
    melee: npcMelee(500, 2000),
    abilities: [MOB_ABILITY.shadeBolt],
    ...critter,
    preferredRange: 22,
    color: '#8fb8ff',
  },
  // Rares (elite, long timers): one per region.
  {
    id: MOB.oldMossback,
    name: 'Old Mossback',
    tier: 'big',
    hpMult: 0.24,
    forces: 0,
    melee: npcMelee(1500, 2400),
    abilities: [MOB_ABILITY.tuskCharge],
    radius: 1.6,
    combatReach: 2,
    moveSpeed: 7,
    tauntDr: false,
    color: '#6f7a4a',
  },
  {
    id: MOB.oldSnapjaw,
    name: 'Old Snapjaw',
    tier: 'big',
    hpMult: 0.26,
    forces: 0,
    melee: npcMelee(1600, 2400),
    abilities: [MOB_ABILITY.clawSnap],
    radius: 1.5,
    combatReach: 2,
    moveSpeed: 6,
    tauntDr: false,
    color: '#e0703a',
  },
  {
    id: MOB.foremanTallowick,
    name: 'Foreman Tallowick',
    tier: 'big',
    hpMult: 0.28,
    forces: 0,
    melee: npcMelee(1700, 2400),
    abilities: [MOB_ABILITY.pickSwing, MOB_ABILITY.candleFlare],
    radius: 1.2,
    combatReach: 2,
    moveSpeed: 7,
    tauntDr: false,
    color: '#d0a040',
  },
  {
    id: MOB.greymaw,
    name: 'Greymaw',
    tier: 'big',
    hpMult: 0.28,
    forces: 0,
    melee: npcMelee(1800, 1800),
    abilities: [MOB_ABILITY.lungingBite],
    radius: 1.4,
    combatReach: 2,
    moveSpeed: 8,
    tauntDr: false,
    color: '#5a6066',
  },
  {
    id: MOB.paleWarden,
    name: 'The Pale Warden',
    tier: 'big',
    hpMult: 0.32,
    forces: 0,
    melee: npcMelee(2000, 2400),
    abilities: [MOB_ABILITY.stoneSlam, MOB_ABILITY.shadeBolt],
    radius: 1.6,
    combatReach: 2.2,
    moveSpeed: 6.5,
    tauntDr: false,
    color: '#eae6da',
  },
];
